import {
  Cube2COrder,
  Cube2BOrder,
  TMSOrderStatus,
  TMSScanOrderVo,
  IntegrationOrderType,
  Cube2COrderInstructionState,
} from "@/types";
import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Modal, Steps } from "antd";
import {
  getTMSOrder,
  getCubeOrderDetail,
} from "@/pages/tms/order/process/helper";

export interface OrderLoadResult {
  tmsOrder: TMSScanOrderVo;
  cubeOrder: Cube2COrder | Cube2BOrder | null;
}

export function OrderLoadingDialog({
  riiOrderNo,
  onLoaded,
  onGoBack,
}: {
  riiOrderNo: string;
  onLoaded: (result: OrderLoadResult) => void;
  onGoBack: () => void;
}) {
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingError, setLoadingError] = useState<string | null>(null);

  const checkOrder = useCallback(
    (
      tmsOrder: TMSScanOrderVo,
      cubeOrder: Cube2COrder | Cube2BOrder,
    ): [boolean, string | null] => {
      if (tmsOrder.status === TMSOrderStatus.Deleted) {
        return [false, "Order is deleted"];
      }
      if (tmsOrder.status === TMSOrderStatus.Completed) {
        return [false, "Order is completed"];
      }

      let orderType: IntegrationOrderType | null = null;
      if (
        tmsOrder.riiOrderNo.startsWith("OTRIIOTIN") ||
        tmsOrder.riiOrderNo.startsWith("OTRIICION")
      ) {
        orderType = IntegrationOrderType.Customer;
      } else if (tmsOrder.riiOrderNo.startsWith("OTRIISUB")) {
        orderType = IntegrationOrderType.Business;
      } else {
        return [false, "Invalid order type"];
      }

      let orderState: Cube2COrderInstructionState;
      if (orderType === IntegrationOrderType.Customer) {
        orderState = (cubeOrder as Cube2COrder).instruction.state;
      } else {
        orderState = (cubeOrder as Cube2BOrder).state;
      }

      if (
        ![
          Cube2COrderInstructionState.PENDING_OUTBOUND,
          Cube2COrderInstructionState.DISTRIBUTION,
          Cube2COrderInstructionState.DISTRIBUTION_PACKING,
          Cube2COrderInstructionState.PROCESSING,
          Cube2COrderInstructionState.WAIT_DISTRIBUTION,
          Cube2COrderInstructionState.WAIT_PACK,
        ].includes(orderState)
      ) {
        return [
          false,
          "Cube order is in state: " + orderState + ", cannot process order!",
        ];
      }

      let customerNumber: string | null = null;
      if (orderType === IntegrationOrderType.Customer) {
        customerNumber = (cubeOrder as Cube2COrder).instruction.theThirdNumber;
      } else if (orderType === IntegrationOrderType.Business) {
        customerNumber = (cubeOrder as Cube2BOrder).amazonFreightNumber;
      }
      if (!customerNumber) {
        return [false, "Invalid order number"];
      }
      return [true, null];
    },
    [],
  );

  const loadOrder = useCallback(
    async (orderNo: string) => {
      setLoadingError(null);
      setLoadingStep(0);

      try {
        const tms = await getTMSOrder(orderNo);
        if (!tms) {
          setLoadingError("TMS Order not found");
          return;
        }
        const cube = await getCubeOrderDetail(orderNo.substring(5));
        if (!cube) {
          setLoadingError("Cube Order not found");
          return;
        }
        setLoadingStep(1);
        const [flag, error] = checkOrder(tms, cube);
        if (!flag) {
          setLoadingError(error);
          return;
        }
        setLoadingStep(2);

        onLoaded({ tmsOrder: tms, cubeOrder: cube });
      } catch (e: any) {
        setLoadingError(e.msg ?? e.message ?? "Failed to load order");
      }
    },
    [onLoaded, checkOrder],
  );

  useEffect(() => {
    if (riiOrderNo) {
      loadOrder(riiOrderNo);
    }
  }, [riiOrderNo, loadOrder]);

  return (
    <Modal
      open
      title="Loading and checking order..."
      footer={
        loadingError ? (
          <Button type="primary" onClick={onGoBack}>
            Close
          </Button>
        ) : null
      }
      closable={false}
      maskClosable={false}
    >
      <Steps
        direction="vertical"
        size="small"
        current={loadingStep}
        status={loadingError ? "error" : "process"}
        items={[
          { title: "Fetching Order" },
          { title: "Checking Order Status" },
          { title: "Done" },
        ]}
      />
      {loadingError ? (
        <Alert message={loadingError} type="error" showIcon />
      ) : null}
    </Modal>
  );
}
